import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Review {
  id: string;
  name: string;
  initials: string;
  date: string;
  text: string;
  avatarColor: string;
}

const REVIEWS: Review[] = [
  {
    id: "katarzyna",
    name: "Katarzyna K.",
    initials: "KK",
    date: "3 maj",
    avatarColor: "bg-accent-500",
    text: "Mateusz, człowiek-rzeka wiadomości o wyspie i z ogromnym potencjałem w jej odkryciu.\nPolecam serdecznie, 100% zadowolenia!",
  },
  {
    id: "magda",
    name: "Magda G.",
    initials: "MG",
    date: "1 dzień temu",
    avatarColor: "bg-primary-600",
    text: "Polecam w 10000%! Punktualność, poczucie humoru i dopasowanie się pod klienta! Noo cechy idealne, które każdy poszukuje w swoim przewodniku! Mateo podjechał pod hotel, odebrał, objechał z nami najpiękniejsze miejsca wokół TEIDY, pokazał zakamarki, te najpiękniejsze, zabrał na kanaryjską kawkę. Mieliśmy szczęście że trafiliśmy właśnie na niego!",
  },
  {
    id: "magdalena",
    name: "Magdalena B.",
    initials: "MB",
    date: "2 cze",
    avatarColor: "bg-accent-600",
    text: "Zdecydowanie polecamy usługi Mateo Tenerife! Od samego początku – od odbioru z lotniska, aż po powrót – wszystko przebiegło sprawnie i bezstresowo. Mateo zawsze był w kontakcie, punktualny i pomocny.\n\nNajlepsza anegdota z naszej wycieczki wydarzyła się w wiosce Masca. Byliśmy przekonani, że znalezienie wolnego miejsca parkingowego graniczy tam z cudem. Już przygotowywaliśmy się na długie krążenie i nerwy, a tu Mateo, z typowym dla siebie spokojem, zaparkował auto tuż obok drzewa, na liniach – dokładnie tam, gdzie tylko nieliczni wiedzą, że można to zrobić. To miejsce wydawało się wręcz niemożliwe do znalezienia dla zwykłego turysty! Właśnie takie doświadczenie i znajomość wyspy sprawiają, że podróż z Mateo to czysta przyjemność.\n\nSamochód czysty, zadbany, komfortowy, a Mateo zawsze uśmiechnięty i pełen ciekawych opowieści o Teneryfie. Polecamy każdemu, kto chce poznać prawdziwe oblicze wyspy i poczuć się jak miejscowy!",
  },
  {
    id: "malgorzata",
    name: "Małgorzata O.",
    initials: "MO",
    date: "27 mar",
    avatarColor: "bg-primary-500",
    text: "Mateusz zorganizował nam 3 wspaniałe objazdowe wycieczki po Teneryfie z pokazaniem najciekawszych punktów z odbiorem spod samego hotelu. Był rejs na delfiny z klifami Los Gigantes i kąpiel w oceanie, widok na czarną plażę i klify, spacer po Masce i pod Teide, Loro Park, spacer po Garachicco i Icod de Los Vinos oraz sesja na plantacji bananów, góry Anaga, Candelaria, palmiarnia w Santa Cruz. Mateo wspaniały kierowca oraz cierpliwy człowiek o dobrym sercu. Polecamy odkrywanie uroków Teneryfy z Mateuszem.",
  },
];

const TRUNCATE_LENGTH = 220;

function ReviewCard({ review }: { review: Review }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > TRUNCATE_LENGTH;
  const displayed = isLong && !expanded
    ? review.text.slice(0, TRUNCATE_LENGTH).trimEnd() + "…"
    : review.text;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 flex flex-col gap-4 hover:border-accent-200 transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${review.avatarColor} flex items-center justify-center flex-shrink-0`}>
            <span className="text-background-50 text-xs font-bold tracking-wide">{review.initials}</span>
          </div>
          <div>
            <div className="font-medium text-foreground-950 text-sm">{review.name}</div>
            <div className="text-xs text-foreground-500 flex items-center gap-1 mt-0.5">
              <i className="ri-facebook-fill text-[#1877F2]"></i>
              <span>{t("tm_source_fb")} · {review.date}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {[1,2,3,4,5].map((i) => (
            <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
          ))}
        </div>
      </div>

      {/* Review text */}
      <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line flex-1">
        {displayed}
      </p>

      {isLong && (
        <button
          onClick={() => setExpanded((s) => !s)}
          className="text-xs font-medium text-accent-600 hover:text-accent-700 transition-colors cursor-pointer self-start"
        >
          {expanded ? t("tm_show_less") : t("tm_show_more")}
        </button>
      )}
    </div>
  );
}

export default function Testimonials() {
  const { t } = useTranslation();

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-accent-600 font-medium mb-3">
            {t("tm_badge")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-950 mb-4">
            {t("tm_heading")}
          </h2>
          <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
            {t("tm_subtitle")}
          </p>
          {/* Overall rating bar */}
          <div className="flex items-center justify-center gap-2 mt-5">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map((i) => (
                <i key={i} className="ri-star-fill text-accent-500 text-base"></i>
              ))}
            </div>
            <span className="font-heading text-xl font-semibold text-foreground-950">5.0</span>
            <span className="text-sm text-foreground-500">· {t("tm_based_on")}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {REVIEWS.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>

        {/* Facebook CTA */}
        <div className="mt-8 text-center">
          <a
            href="https://www.facebook.com/share/18s1Yq8cTT/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 border border-background-200/70 text-foreground-800 text-sm font-medium px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-facebook-fill text-[#1877F2] text-base"></i>
            {t("tm_see_more_fb")}
          </a>
        </div>
      </div>
    </section>
  );
}